import { HttpErrorResponse } from "@angular/common/http";
import { ErrorHandler, Injectable, Injector, NgZone } from "@angular/core";

import { NotificationService } from "@core/services/notification.service";
import { ToasterSeverities } from "@enums/toaster-severities";

/**
 * Catches errors that no `catchError` handled — uncaught runtime exceptions and
 * rejected promises. Without this, such errors only reach the console and the
 * user is left staring at a UI that silently stopped working. We log the raw
 * error (for developers) and show a clean, translated toast (for the user).
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  constructor(private injector: Injector) {}

  handleError(error: unknown): void {
    // Always log the original error so the full stack trace is preserved.
    console.error(error);

    const actual = this.unwrap(error);

    // Dev-only framework warnings (e.g. NG0100 ExpressionChangedAfterItHasBeen-
    // Checked) are stripped from production builds and are not user-actionable,
    // so we log them but never bother the user with a toast.
    if (this.isDevOnlyFrameworkError(actual)) {
      return;
    }

    // A failed lazy-chunk load almost always means a new build was deployed
    // while the user had the old page open — a reload fixes it.
    const messageKey = this.isChunkLoadError(actual)
      ? "ERROR_NEW_VERSION"
      : "UNKNOWN_ERROR";

    // HTTP errors are normally surfaced by effects; if one slips through we let
    // the notification service extract its detail, otherwise show no detail
    // (we never expose raw stack traces to the user).
    const information =
      actual instanceof HttpErrorResponse ? actual : undefined;

    this.notify(messageKey, information);
  }

  private notify(messageKey: string, information: unknown): void {
    // ErrorHandler is created eagerly during bootstrap, so resolve dependencies
    // lazily to avoid a circular DI graph. Errors can also be thrown outside
    // Angular's zone, so re-enter it to make the toast render.
    const zone = this.injector.get(NgZone);
    const notificationService = this.injector.get(NotificationService);

    zone.run((): void => {
      notificationService.show(
        ToasterSeverities.Error,
        messageKey,
        information
      );
    });
  }

  /** Unhandled promise rejections arrive wrapped in a `{ rejection }` object. */
  private unwrap(error: unknown): unknown {
    return error && typeof error === "object" && "rejection" in error
      ? (error as { rejection: unknown }).rejection
      : error;
  }

  private isChunkLoadError(error: unknown): boolean {
    const message =
      error instanceof Error ? error.message : String(error ?? "");

    return /ChunkLoadError|Loading chunk [\d]+ failed|dynamically imported module/i.test(
      message
    );
  }

  private isDevOnlyFrameworkError(error: unknown): boolean {
    const message =
      error instanceof Error ? error.message : String(error ?? "");

    // NG0100 = ExpressionChangedAfterItHasBeenCheckedError (development only).
    return /NG0100|ExpressionChangedAfterItHasBeenChecked/i.test(message);
  }
}
